import { useState, useCallback, useEffect } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Search, Users, RefreshCw, Snowflake, Play } from 'lucide-react'
import { toast } from 'sonner'
import { getCustomerList, toggleCustomerCreditFreeze } from '@/src/lib/api/admin-analytics'
import type { AdminCustomerListResponse } from '@/src/types/admin/analytics'

export default function AdminUsers() {
  const [customers, setCustomers] = useState<AdminCustomerListResponse | null>(null)
  const [loading, setLoading] = useState(false)
  const [page, setPage] = useState(1)
  const [search, setSearch] = useState('')
  const [packageFilter, setPackageFilter] = useState('')
  const [kycFilter, setKycFilter] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  // Freeze dialog state
  const [freezeTarget, setFreezeTarget] = useState<{ id: string; email: string; freeze: boolean } | null>(null)
  const [freezeLoading, setFreezeLoading] = useState(false)

  const fetchCustomers = useCallback(async () => {
    setLoading(true)
    try {
      const data = await getCustomerList({
        page,
        limit: 20,
        search: search || undefined,
        package_code: packageFilter || undefined,
        kyc_status: kycFilter || undefined,
        is_active: statusFilter === '' ? undefined : statusFilter === 'active',
      })
      setCustomers(data)
    } finally {
      setLoading(false)
    }
  }, [page, search, packageFilter, kycFilter, statusFilter])

  useEffect(() => {
    fetchCustomers()
  }, [fetchCustomers])

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    setPage(1)
    fetchCustomers()
  }

  const handleConfirmFreeze = async () => {
    if (!freezeTarget) return
    setFreezeLoading(true)
    try {
      await toggleCustomerCreditFreeze(freezeTarget.id, freezeTarget.freeze)
      setCustomers(prev =>
        prev
          ? {
              ...prev,
              data: prev.data.map(c =>
                c.customer_id === freezeTarget.id
                  ? { ...c, credit_usage_frozen: freezeTarget.freeze }
                  : c
              ),
            }
          : prev
      )
      toast.success(freezeTarget.freeze ? 'ระงับการใช้เครดิตเรียบร้อยแล้ว' : 'ปลดระงับการใช้เครดิตเรียบร้อยแล้ว')
    } catch {
      toast.error('เกิดข้อผิดพลาด ไม่สามารถเปลี่ยนสถานะเครดิตได้ กรุณาลองใหม่')
    } finally {
      setFreezeLoading(false)
      setFreezeTarget(null)
    }
  }

  const kycBadgeClass = (status?: string) => {
    if (status === 'approved') return 'border-green-400 bg-green-100 text-green-700 dark:bg-green-950 dark:text-green-400 dark:border-green-700'
    if (status === 'rejected') return 'border-red-400 bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-400 dark:border-red-700'
    if (status === 'pending')  return 'border-amber-400 bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-400 dark:border-amber-700'
    return 'border-muted-foreground/30 text-muted-foreground'
  }

  const kycLabel = (status?: string) => {
    if (status === 'approved') return 'อนุมัติ'
    if (status === 'rejected') return 'ปฏิเสธ'
    if (status === 'pending') return 'รอตรวจ'
    return '—'
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">จัดการผู้ใช้</h1>
        <p className="text-muted-foreground">จัดการข้อมูลลูกค้าและสถานะการใช้งาน</p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            รายการลูกค้าทั้งหมด
            {customers && (
              <span className="ml-auto text-sm font-normal text-muted-foreground">
                {customers.pagination.total.toLocaleString()} รายการ
              </span>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Filters */}
          <form onSubmit={handleSearch} className="flex flex-wrap gap-2">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="ค้นหาอีเมล / เบอร์โทร / ชื่อธุรกิจ..."
                className="pl-8"
                value={search}
                onChange={e => setSearch(e.target.value)}
              />
            </div>
            <Select value={packageFilter} onValueChange={v => { setPackageFilter(v === 'all' ? '' : v); setPage(1) }}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="แพ็กเกจ" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">ทุกแพ็กเกจ</SelectItem>
                <SelectItem value="FREE">FREE</SelectItem>
                <SelectItem value="STARTER">STARTER</SelectItem>
                <SelectItem value="PROFESSIONAL">PROFESSIONAL</SelectItem>
                <SelectItem value="ENTERPRISE">ENTERPRISE</SelectItem>
              </SelectContent>
            </Select>
            <Select value={kycFilter} onValueChange={v => { setKycFilter(v === 'all' ? '' : v); setPage(1) }}>
              <SelectTrigger className="w-[130px]">
                <SelectValue placeholder="KYC" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">ทุก KYC</SelectItem>
                <SelectItem value="pending">รอดำเนินการ</SelectItem>
                <SelectItem value="approved">อนุมัติแล้ว</SelectItem>
                <SelectItem value="rejected">ปฏิเสธ</SelectItem>
              </SelectContent>
            </Select>
            <Select value={statusFilter} onValueChange={v => { setStatusFilter(v === 'all' ? '' : v); setPage(1) }}>
              <SelectTrigger className="w-[130px]">
                <SelectValue placeholder="สถานะ" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">ทุกสถานะ</SelectItem>
                <SelectItem value="active">ใช้งาน</SelectItem>
                <SelectItem value="inactive">ระงับ</SelectItem>
              </SelectContent>
            </Select>
            <Button type="submit" size="sm">ค้นหา</Button>
            <Button type="button" variant="outline" size="sm" onClick={fetchCustomers}>
              <RefreshCw className="h-4 w-4" />
            </Button>
          </form>

          {/* Table */}
          {loading ? (
            <div className="space-y-2">
              {Array.from({ length: 8 }).map((_, i) => <Skeleton key={i} className="h-12 w-full" />)}
            </div>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>ลูกค้า</TableHead>
                    <TableHead>เบอร์โทร</TableHead>
                    <TableHead>แพ็กเกจ</TableHead>
                    <TableHead>KYC</TableHead>
                    <TableHead className="text-right">เครดิต</TableHead>
                    <TableHead>สมัครเมื่อ</TableHead>
                    <TableHead>เข้าใช้ล่าสุด</TableHead>
                    <TableHead>สถานะ</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {customers?.data.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={9} className="text-center text-muted-foreground py-10">
                        ไม่พบข้อมูลลูกค้า
                      </TableCell>
                    </TableRow>
                  ) : customers?.data.map(c => (
                    <TableRow key={c.customer_id} className={!c.is_active ? 'opacity-60' : c.credit_usage_frozen ? 'bg-blue-50/40 dark:bg-blue-950/20' : ''}>
                      <TableCell>
                        <div className="text-sm font-medium truncate max-w-[180px]">{c.email}</div>
                        {c.business_name && (
                          <div className="text-xs text-muted-foreground truncate max-w-[180px]">
                            {c.business_name}
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">{c.phone || '—'}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className="text-xs">{c.package_code}</Badge>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className={`text-xs ${kycBadgeClass(c.kyc_status)}`}>
                          {kycLabel(c.kyc_status)}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right text-sm">
                        {c.credit_balance.toLocaleString('th-TH')}
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground whitespace-nowrap">
                        {new Date(c.created_at).toLocaleDateString('th-TH')}
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground whitespace-nowrap">
                        {c.last_login_at ? new Date(c.last_login_at).toLocaleDateString('th-TH') : '—'}
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-col gap-1">
                          <Badge variant={c.is_active ? 'secondary' : 'outline'} className="text-xs w-fit">
                            {c.is_active ? 'ใช้งาน' : 'ระงับ'}
                          </Badge>
                          {c.credit_usage_frozen && (
                            <Badge variant="outline" className="text-xs w-fit border-blue-400 text-blue-600 dark:text-blue-400">
                              <Snowflake className="h-3 w-3 mr-1" />
                              ระงับเครดิต
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant={c.credit_usage_frozen ? 'outline' : 'ghost'}
                          size="sm"
                          className={c.credit_usage_frozen
                            ? 'text-green-600 border-green-300 hover:bg-green-50 dark:hover:bg-green-950 h-7 px-2 text-xs'
                            : 'text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-950 h-7 px-2 text-xs'
                          }
                          onClick={() => setFreezeTarget({ id: c.customer_id, email: c.email, freeze: !c.credit_usage_frozen })}
                        >
                          {c.credit_usage_frozen
                            ? <><Play className="h-3 w-3 mr-1" />ปลดระงับเครดิต</>
                            : <><Snowflake className="h-3 w-3 mr-1" />ระงับเครดิต</>
                          }
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {/* Pagination */}
              {customers && customers.pagination.total_pages > 1 && (
                <div className="flex items-center justify-between text-sm text-muted-foreground">
                  <span>{customers.pagination.total.toLocaleString()} รายการ</span>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(p => Math.max(1, p - 1))}
                      disabled={page === 1}
                    >
                      ก่อนหน้า
                    </Button>
                    <span className="px-2">{page} / {customers.pagination.total_pages}</span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(p => Math.min(customers.pagination.total_pages, p + 1))}
                      disabled={page === customers.pagination.total_pages}
                    >
                      ถัดไป
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Freeze/Unfreeze confirmation dialog */}
      <AlertDialog open={!!freezeTarget} onOpenChange={(open) => { if (!open) setFreezeTarget(null) }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {freezeTarget?.freeze ? 'ระงับการใช้เครดิต' : 'ปลดระงับการใช้เครดิต'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {freezeTarget?.freeze
                ? `ลูกค้า "${freezeTarget?.email}" จะไม่สามารถใช้เครดิตเพื่อเรียก API ได้จนกว่าจะปลดระงับ แต่ยังสามารถดูประวัติและข้อมูลบัญชีได้ตามปกติ`
                : `ลูกค้า "${freezeTarget?.email}" จะกลับมาใช้เครดิต API ได้ตามปกติ`
              }
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={freezeLoading}>ยกเลิก</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmFreeze}
              disabled={freezeLoading}
              className={freezeTarget?.freeze
                ? 'bg-blue-600 hover:bg-blue-700 text-white'
                : 'bg-green-600 hover:bg-green-700 text-white'
              }
            >
              {freezeLoading ? 'กำลังดำเนินการ...' : (freezeTarget?.freeze ? 'ยืนยันระงับเครดิต' : 'ยืนยันปลดระงับ')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}

